import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getBranchesThunk,
  copyConfigurationThunk,
} from "../../../store/thunks/branchThunks";
import { XIcon, DocumentDuplicateIcon } from "@heroicons/react/outline";

const BranchSelectionModal = ({
  isOpen,
  onClose,
  targetBranchId,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const { branches, loadingList } = useSelector((state) => state.branch);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      dispatch(getBranchesThunk({ isActive: true }));
    }
  }, [isOpen, dispatch]);

  const handleCopyConfiguration = async () => {
    if (!selectedBranch || !targetBranchId) return;

    setCopying(true);
    try {
      await dispatch(
        copyConfigurationThunk({
          sourceBranchId: selectedBranch,
          targetBranchId: targetBranchId,
        })
      ).unwrap();

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to copy configuration:", error);
    } finally {
      setCopying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-black opacity-50"
          onClick={onClose}
        ></div>

        <div className="relative bg-white rounded-lg max-w-2xl w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold flex items-center">
              <DocumentDuplicateIcon className="h-6 w-6 mr-2" />
              Copy Branch Configuration
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          <p className="text-gray-600 mb-4">
            Select a branch to copy configuration from:
          </p>

          {loadingList ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {branches
                .filter((branch) => branch._id !== targetBranchId)
                .map((branch) => (
                  <div
                    key={branch._id}
                    onClick={() => setSelectedBranch(branch._id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedBranch === branch._id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{branch.name}</h3>
                        <p className="text-sm text-gray-500">{branch.code}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {branch.address.city}, {branch.address.state}
                        </p>
                      </div>
                      <div className="text-right">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            branch.branchType === "FLAGSHIP"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {branch.branchType}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          Mode: {branch.rateConfig?.rateMode || "ADMIN"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}

          <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This will copy rate configuration,
              settings, and working hours from the selected branch.
            </p>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCopyConfiguration}
              disabled={!selectedBranch || copying}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {copying ? "Copying..." : "Copy Configuration"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchSelectionModal;
